import styled from "@emotion/styled";
import { css } from "@emotion/react";
import type { AnchorHTMLAttributes, FC } from "react";
import { colors, fonts, radius, spacing } from "../design-tokens";

// Common typography base styles
const baseTypography = css`
  ${fonts.bodyFont}
  font-style: normal;
  color: ${colors.text};
`;

const baseHeadingTypography = css`
  ${fonts.headingFont}
  font-style: normal;
  color: ${colors.text};
`;

// Base heading styles (600 weight)
const headingTypography = css`
  ${baseHeadingTypography}
  font-weight: 600;
  margin: 0;
  text-wrap: balance;
`;

// Base body text styles (400 weight)
const baseBodyTypography = css`
  ${baseTypography}
  font-weight: 400;
  margin: 0;
  text-wrap: pretty;
`;

// Display Typography
export const Display = styled.h1`
  ${headingTypography}
  font-size: 64px;
  line-height: 100%; /* 64px */
`;

// Heading 1
export const H1 = styled.h1`
  ${headingTypography}
  font-size: 40px;
  line-height: 100%; /* 40px */
`;

// Heading 2
export const H2 = styled.h2`
  ${headingTypography}
  font-size: 32px;
  line-height: 100%; /* 32px */
`;

// Heading 3
export const H3 = styled.h3`
  ${headingTypography}
  font-size: 24px;
  line-height: 120%; /* 28.8px */
`;

// Heading 4
export const H4 = styled.h4`
  ${headingTypography}
  font-size: 22px;
  line-height: 120%; /* 26.4px */
`;

// Heading 5
export const H5 = styled.h5`
  ${headingTypography}
  font-size: 18px;
  line-height: 120%; /* 21.6px */
`;

// Heading 6
export const H6 = styled.h6`
  ${headingTypography}
  font-size: 16px;
  line-height: 120%; /* 19.2px */
`;

// Paragraph Regular
export const ParagraphRegular = styled.p`
  ${baseBodyTypography}
  font-size: 16px;
  line-height: 150%; /* 24px */
`;

// Paragraph Small
export const ParagraphSmall = styled.p`
  ${baseBodyTypography}
  font-size: 14px;
  line-height: 120%; /* 16.8px */
`;

// Standard Text (same as Paragraph Regular)
export const StandardText = styled.span`
  ${baseBodyTypography}
  font-size: 16px;
  line-height: 150%; /* 24px */
`;

// Link Text
export const LinkText = styled.a`
  ${baseTypography}
  color: ${colors.primary}; /* #002263 */
  font-size: 16px;
  font-weight: 600;
  line-height: 150%; /* 24px */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
  cursor: pointer;

  &:hover {
    color: ${colors.primaryHover};
  }
`;

// Alternative component for spans that should look like links but aren't clickable
export const LinkTextSpan = styled.span`
  ${baseTypography}
  color: ${colors.primary}; /* #002263 */
  font-size: 16px;
  font-weight: 600;
  line-height: 150%; /* 24px */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

export const NewTabBodyLink: FC<AnchorHTMLAttributes<HTMLAnchorElement>> = ({
  children,
  href,
  ...props
}) => {
  // For tel: and mailto: links, don't open in new tab
  const isExternalProtocol =
    href && (href.startsWith("tel:") || href.startsWith("mailto:"));

  if (isExternalProtocol) {
    return (
      <LinkText href={href} {...props}>
        {children}
      </LinkText>
    );
  }

  // For regular http/https links, open in new tab to preserve chat context
  return (
    <LinkText href={href} {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </LinkText>
  );
};

export const Blockquote = styled.blockquote`
  background-color: ${colors.background};
  padding: ${spacing.lg}px;
  border-radius: ${radius.lg}px;
  margin-bottom: ${spacing["3xl"]}px;
`;

// Styled list with proper typography
export const BodyOrderedList = styled.ol`
  ${baseBodyTypography}
  font-size: 16px;
  line-height: 150%; /* 24px */
  margin: ${spacing.md}px 0;
  padding-left: ${spacing.xl}px;
`;

// Styled list with proper typography
export const BodyUnorderedList = styled.ul`
  ${baseBodyTypography}
  font-size: 16px;
  line-height: 150%; /* 24px */
  margin: ${spacing.md}px 0;
  padding-left: ${spacing.xl}px;
`;

// Styled list item
export const BodyListItem = styled.li`
  ${baseBodyTypography}
  font-size: 16px;
  line-height: 150%; /* 24px */
  margin-bottom: ${spacing.xs}px;
`;
